/**
 * @fileoverview Entry point for the Storyteller web application.
 * This file initializes the application, sets up i18n, and renders the main App component.
 *
 * @example
 * // Basic usage
 * <App language="en" assignmentId="123" token="abc" onLanguageChange={() => Promise.resolve()} />
 */

import { createRoot } from 'react-dom/client'
import { App } from './App'

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { GlobalModalContext } from './GlobalModalContext'
import enARIBIO from './i18n/en-ARIBIO.json'
import enATRIA from './i18n/en_ATRIA.json'
import enCLINCLOUD from './i18n/en_CLINCLOUD.json'
import enGB from './i18n/en_GB.json'
import enSTUDYKIK from './i18n/en_STUDYKIK.json'
import enUS from './i18n/en_US.json'
import esCLINCLOUD from './i18n/es_CLINCLOUD.json'
import esSTUDYKIK from './i18n/es_STUDYKIK.json'
import esUS from './i18n/es_US.json'

const params = new URLSearchParams(location.search)
const language = params.get('language') || 'en-US'
const assignmentId = params.get('assignment_id')
const token = params.get('token')

/**
 * @function formatLanguage
 * @description Formats the language string to match the expected format for i18n.
 * @param {string} language - The language string to format.
 * @returns {string} The formatted language string.
 */
function formatLanguage(language: string): string {
    return language.replace('-', '_')
}

// Expose the git sha to the window object so we can always check deployed revision
;(window as Window & typeof globalThis & Record<string, Record<string, string>>).storyteller = {
    version: process.env.GIT_SHA,
    timestamp: process.env.GIT_TIMESTAMP,
    branch: process.env.GIT_BRANCH,
}

i18n.use(initReactI18next).init({
    resources: {
        en_US: {
            translation: enUS,
        },
        es_US: {
            translation: esUS,
        },
        en_GB: {
            translation: enGB,
        },
        en_ARIBIO: {
            translation: enARIBIO,
        },
        en_ATRIA: {
            translation: enATRIA,
        },
        en_CLINCLOUD: {
            translation: enCLINCLOUD,
        },
        es_CLINCLOUD: {
            translation: esCLINCLOUD,
        },
        en_STUDYKIK: {
            translation: enSTUDYKIK,
        },
        es_STUDYKIK: {
            translation: esSTUDYKIK,
        }
    },
    lng: formatLanguage(language),
    fallbackLng: 'en_US',
})

/**
 * @function onLanguageChange
 * @description Handles language change events by updating the i18n instance and returning a promise.
 * @param {string} language - The new language to set.
 * @returns {Promise<void>} A promise that resolves when the language change is complete.
 */
function onLanguageChange(language: string): Promise<void> {
    i18n.changeLanguage(language)
    return Promise.resolve()
}

const root = createRoot(document.getElementById('app'))
root.render(
    <GlobalModalContext>
        <App
            language={formatLanguage(language)}
            onLanguageChange={onLanguageChange}
            assignmentId={assignmentId}
            token={token}
        />
    </GlobalModalContext>
)
